import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Video, IndianRupee } from 'lucide-react';
import axios from 'axios';
import { KeyFeaturesList } from '../components/KeyFeatures';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { RegistrationModal } from '../components/RegistrationModal';
import { useLanguage, useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priceBadge = (item, tr) => {
  if (item.pricing_mode === 'fee') {
    return `₹${((item.fee_amount || 0) / 100).toLocaleString('en-IN')} · ${tr('Fee')}`;
  }
  if (item.pricing_mode === 'dakshina') return tr('Dakshina');
  return tr('Free');
};

export const Events = () => {
  usePageTitle('Events & Satsangs');
  const tr = useT();
  const { stateScope } = useLanguage();
  const [events, setEvents] = useState([]);
  const [satsangs, setSatsangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regTarget, setRegTarget] = useState(null);

  useEffect(() => {
    fetchData();
  }, [stateScope]);

  const fetchData = async () => {
    try {
      const [eventsRes, satsangsRes] = await Promise.all([
        axios.get(`${API_URL}/api/events`, { params: { state: stateScope } }),
        axios.get(`${API_URL}/api/satsangs`, { params: { state: stateScope } }),
      ]);
      setEvents(eventsRes.data);
      setSatsangs(satsangsRes.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="events-page">
      <section className="py-24 md:py-32 bg-gradient-to-br from-saffron-50 to-gold-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {tr('Spiritual Gatherings')}
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {tr('Events & Satsangs')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {tr('Join our community for webinars, spiritual gatherings, and festival celebrations.')}
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl font-heading text-charcoal-600">{tr('Loading events...')}</div>
            </div>
          ) : (
            <>
              {events.length > 0 && (
                <div className="mb-20">
                  <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                    {tr('Upcoming Events')}
                  </h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    {events.map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden"
                        data-testid={`event-card-${index}`}
                      >
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-6">
                          <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-3">
                            {tr(event.title)}
                          </h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed mb-4">
                            {tr(event.description)}
                          </p>
                          <KeyFeaturesList features={event.key_features} tr={tr} testId={`event-features-${index}`} />
                          <div className="space-y-2 text-sm text-charcoal-600">
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-saffron-600" />
                              <span>{tr(event.event_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-saffron-600" />
                              <span>{tr(event.event_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {event.is_online ? (
                                <>
                                  <Video size={16} className="text-saffron-600" />
                                  <span>{tr('Online Event')}</span>
                                </>
                              ) : (
                                <>
                                  <MapPin size={16} className="text-saffron-600" />
                                  <span>{tr(event.location)}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <IndianRupee size={16} className="text-saffron-600" />
                              <span className="font-medium">{priceBadge(event, tr)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => setRegTarget({ item: event, kind: 'event' })}
                            data-testid={`event-register-${index}`}
                            className="mt-5 w-full bg-saffron-600 text-white rounded-full px-6 py-3 font-body font-medium hover:bg-saffron-500 transition-colors shadow"
                          >
                            {tr('Register Now')}
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {satsangs.length > 0 && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                    {tr('Satsangs')}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {satsangs.map((satsang, index) => (
                      <motion.div
                        key={satsang.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden"
                        data-testid={`satsang-card-${index}`}
                      >
                        <img
                          src={satsang.image_url}
                          alt={satsang.title}
                          className="w-full h-40 object-cover"
                        />
                        <div className="p-6">
                          <h3 className="text-lg font-heading leading-snug text-charcoal-800 mb-2">
                            {tr(satsang.title)}
                          </h3>
                          <p className="text-xs text-charcoal-600 mb-3 line-clamp-2">
                            {tr(satsang.description)}
                          </p>
                          <div className="text-xs text-charcoal-500">
                            <div className="flex items-center gap-2 mb-1">
                              <Calendar size={14} />
                              <span>{tr(satsang.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                              <Clock size={14} />
                              <span>{tr(satsang.time)}</span>
                            </div>
                            <div className="font-semibold text-saffron-600 mt-2">
                              {tr('Speaker')}: {tr(satsang.speaker)}
                            </div>
                            <div className="flex items-center gap-2 text-charcoal-600 mt-1">
                              <IndianRupee size={14} className="text-saffron-600" />
                              <span className="text-xs font-medium">{priceBadge(satsang, tr)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => setRegTarget({ item: satsang, kind: 'satsang' })}
                            data-testid={`satsang-register-${index}`}
                            className="mt-4 w-full bg-saffron-600 text-white rounded-full px-4 py-2 text-sm font-body font-medium hover:bg-saffron-500 transition-colors"
                          >
                            {tr('Register Now')}
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {events.length === 0 && satsangs.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-lg text-charcoal-600">No events scheduled at the moment.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <RegistrationModal
        open={!!regTarget}
        onClose={() => setRegTarget(null)}
        item={regTarget?.item}
        kind={regTarget?.kind}
      />
    </div>
  );
};
